import { TipoTermoAditivo } from '../../../modules/types/Aquisicao';
import { getStatusTALabelPorSlug } from '../../../modules/types/StatusTAEnum';
import { getTipoEnvolvimentoLabelPorSlug } from '../../../modules/types/TipoEnvolvimentoEnum';
import { getTipoFiscalLabelPorSlug } from '../../../modules/types/TipoFiscalEnum';
import { ExportSheet } from '../core';
import { baseLogColumns } from '../factory';

/**
 * Busca os dados dos componentes compartilhados seguindo
 * o padrão: api/{entitySlug}/porentidade?tab={tab}&id={id}
 */
async function fetchGenericEntityData(
  entitySlug: string,
  tab: string,
  proaqIds: number[],
): Promise<any[]> {
  try {
    // Faz requisições para cada Proaq ID
    const promises = proaqIds.map(async (id) => {
      try {
        const response = await fetch(
          `/api/${entitySlug}/porentidade?tab=${tab}&id=${id}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.ok) {
          const data = await response.json();

          return Array.isArray(data.registros) ? data.registros : [];
        }
        return [];
      } catch (error) {
        console.error(`Erro ao buscar ${entitySlug} para Proaq ${id}:`, error);
        return [];
      }
    });

    const results = await Promise.all(promises);
    // Combina todos os arrays em um único array
    return results.flat();
  } catch (error) {
    console.error(`Erro geral ao buscar ${entitySlug}:`, error);
    return [];
  }
}

/**
 * Adiciona sheet de Responsáveis Aquisição se houver dados
 */
function addResponsaveisAquisicaoSheet(
  sheets: ExportSheet[],
  modulo: string,
  responsaveisAquisicao?: any[],
) {
  if (!responsaveisAquisicao?.length) return;
  const moduloUpperCase = modulo.toUpperCase();

  sheets.push({
    name: 'Responsáveis Aquisição',
    data: responsaveisAquisicao.map((respAq) =>
      transformResponsavelAquisicao(respAq, modulo),
    ),
    columns: {
      ...baseLogColumns,
      [`${modulo}Id`]: `ID ${moduloUpperCase}`,
      responsavel: 'Responsável',
      status: 'Status',
      dataInicio: 'Data Início',
      dataFim: 'Data Fim',
      dataRegistro: 'Data Registro',
      observacoes: 'Observações',
    },
  });
}

const transformResponsavelAquisicao = (respAq: any, modulo: string) => ({
  //logs
  id: respAq.id,
  usuarioRegistro: respAq.usuarioRegistro?.nomeCompleto || 'N/A',
  dataRegistro: respAq.dataRegistro,
  dataUltimaAtualizacao: respAq.dataUltimaAtualizacao,
  usuarioAtualizacao: respAq.usuarioAtualizacao?.nomeCompleto || 'N/A',
  nEdicoes: respAq.nEdicoes || 0,
  nVisualizacoes: respAq.nVisualizacoes || 0,
  nVisualizadores: respAq.nVisualizadores || 0,

  [`${modulo}Id`]: respAq[modulo]?.id || respAq[`${modulo}_id`],
  responsavel: respAq.responsavel?.nomeCompleto || 'N/A',
  status: respAq.status == 'S' ? 'Ativo' : 'Inativo',
  dataInicio: respAq.dataInicio || '-',
  dataFim: respAq.dataFim || 'N/A',
  observacoes: respAq.observacoes || '-',
});

/**
 * Adiciona sheet de Tramitações SEI se houver dados
 */
function addTramitacoesSeiSheet(
  sheets: ExportSheet[],
  modulo: string,
  tramitacoesSei?: any[],
) {
  if (!tramitacoesSei?.length) return;
  const moduloUpperCase = modulo.toUpperCase();
  sheets.push({
    name: 'Tramitações SEI',
    data: tramitacoesSei.map((tramitacao) =>
      transformTramitacaoSei(tramitacao, modulo),
    ),
    columns: {
      ...baseLogColumns,
      [`${modulo}Id`]: `ID ${moduloUpperCase}`,
      dataEntrada: 'Data de Entrada',
      dataSaida: 'Data de Saída',
      seiUsuario: 'Usuário SEI',
      seiDescricao: 'Descrição',
      seiUnidade: 'Unidade MS',
      observacoes: 'Observações',
    },
  });
}

const transformTramitacaoSei = (tramitacao: any, modulo: string) => ({
  //logs
  id: tramitacao.id,
  usuarioRegistro: tramitacao.usuarioRegistro?.nomeCompleto || 'N/A',
  dataRegistro: tramitacao.dataRegistro,
  dataUltimaAtualizacao: tramitacao.dataUltimaAtualizacao,
  usuarioAtualizacao: tramitacao.usuarioAtualizacao?.nomeCompleto || 'N/A',
  nEdicoes: tramitacao.nEdicoes || 0,
  nVisualizacoes: tramitacao.nVisualizacoes || 0,
  nVisualizadores: tramitacao.nVisualizadores || 0,

  [`${modulo}Id`]: tramitacao[modulo]?.id || tramitacao[`${modulo}_id`],
  dataEntrada: tramitacao.dataEntrada || 'Não informado',
  dataSaida: tramitacao.dataSaida || 'Não informado',
  seiUsuario: tramitacao.seiUsuario || 'Não informado',
  seiDescricao: tramitacao.seiDescricao || 'Não informado',
  seiUnidade: tramitacao.seiUnidade || 'Não informado',
  observacoes: tramitacao.observacoes || 'Não informado',
});

/**
 * Adiciona sheet de Partes Envolvidas se houver dados
 */
function addPartesEnvolvidasSheet(
  sheets: ExportSheet[],
  modulo: string,
  partesEnvolvidas?: any[],
) {
  if (!partesEnvolvidas?.length) return;

  const moduloUpperCase = modulo.toUpperCase();

  sheets.push({
    name: 'Partes Envolvidas',
    data: partesEnvolvidas.map((parte) =>
      transformParteEnvolvida(parte, modulo),
    ),
    columns: {
      ...baseLogColumns,
      [`${modulo}Id`]: `ID ${moduloUpperCase}`,
      cnpj: 'CNPJ',
      razaoSocial: 'Razão Social',
      tipoEnvolvimento: 'Tipo de Envolvimento',
      observacoes: 'Observações',
    },
  });
}

const transformParteEnvolvida = (parte: any, modulo: string) => ({
  //logs
  id: parte.id,
  usuarioRegistro: parte.usuarioRegistro?.nomeCompleto || 'N/A',
  dataRegistro: parte.dataRegistro,
  dataUltimaAtualizacao: parte.dataUltimaAtualizacao,
  usuarioAtualizacao: parte.usuarioAtualizacao?.nomeCompleto || 'N/A',
  nEdicoes: parte.nEdicoes || 0,
  nVisualizacoes: parte.nVisualizacoes || 0,
  nVisualizadores: parte.nVisualizadores || 0,

  //parte envolvida
  [`${modulo}Id`]: parte[modulo]?.id || parte[`${modulo}_id`],
  cnpj: parte.fornecedor?.cnpj ?? 'Não encontrado',
  razaoSocial: parte.fornecedor?.razaoSocial ?? 'Não encontrado',
  tipoEnvolvimento:
    getTipoEnvolvimentoLabelPorSlug(parte.tipoEnvolvimento) || 'N/A',
  observacoes: parte.observacoes || 'N/A',
});

/**
 * Adiciona sheet de Termos Aditivos se houver dados
 */
function addTermosAditivosSheet(
  sheets: ExportSheet[],
  modulo: string,
  termosAditivos?: any[],
) {
  if (!termosAditivos?.length) return;
  const moduloUpperCase = modulo.toUpperCase();

  sheets.push({
    name: 'Termos Aditivos',
    data: termosAditivos.map((termo) => transformTermoAditivo(termo, modulo)),
    columns: {
      ...baseLogColumns,
      [`${modulo}Id`]: `ID ${moduloUpperCase}`,
      numeroTA: 'Número do Termo',
      documentoSEI: 'Nº Documento SEI',
      dataDOU: 'Data D.O.U.',
      objetoTA: 'Objeto do Termo',
      tipoTA: 'Tipos de Termo',
      status: 'Status',
    },
  });
}

const transformTermoAditivo = (termo: any, modulo: string) => ({
  //logs
  id: termo.id,
  usuarioRegistro: termo.usuarioRegistro?.nomeCompleto || 'N/A',
  dataRegistro: termo.dataRegistro,
  dataUltimaAtualizacao: termo.dataUltimaAtualizacao,
  usuarioAtualizacao: termo.usuarioAtualizacao?.nomeCompleto || 'N/A',
  nEdicoes: termo.nEdicoes || 0,
  nVisualizacoes: termo.nVisualizacoes || 0,
  nVisualizadores: termo.nVisualizadores || 0,

  //termo aditivo
  [`${modulo}Id`]: termo[modulo]?.id || termo[`${modulo}_id`],
  numeroTA: termo.numeroTA || 'N/A',
  status: getStatusTALabelPorSlug(termo.status),
  documentoSEI: termo.documentoSEI || 'N/A',
  dataDOU: termo.dataDOU || 'N/A',
  objetoTA: termo.objetoTA || 'N/A',
  tipoTA: termo.tiposTA
    ? termo.tiposTA.map((tipoTA: TipoTermoAditivo) => tipoTA.nome).join(', ')
    : 'N/A',
  observacoes: termo.observacoes || 'N/A',
});

/**
 * Adiciona sheet de Fiscais Aquisicao se houver dados
 */
function addFiscaisAquisicaoSheet(
  sheets: ExportSheet[],
  modulo: string,
  fiscaisAquisicao?: any[],
) {
  if (!fiscaisAquisicao?.length) return;
  const moduloUpperCase = modulo.toUpperCase();

  sheets.push({
    name: 'Fiscais Aquisicao',
    data: fiscaisAquisicao.map((fiscal) =>
      transformFiscalAquisicao(fiscal, modulo),
    ),
    columns: {
      ...baseLogColumns,
      [`${modulo}Id`]: `ID ${moduloUpperCase}`,
      fiscal: 'Fiscal',
      tipoFiscal: 'Tipo de Fiscalização',
      status: 'Status',
      dataInicio: 'Data Início',
      dataFim: 'Data Término',
      documentoSei: 'Documento SEI',
      link: 'Link',
      dataRegistro: 'Data Registro',
      observacoes: 'Observações',
    },
  });
}

const transformFiscalAquisicao = (fiscal: any, modulo: string) => ({
  //logs
  id: fiscal.id,
  usuarioRegistro: fiscal.usuarioRegistro?.nomeCompleto || 'N/A',
  dataRegistro: fiscal.dataRegistro,
  dataUltimaAtualizacao: fiscal.dataUltimaAtualizacao,
  usuarioAtualizacao: fiscal.usuarioAtualizacao?.nomeCompleto || 'N/A',
  nEdicoes: fiscal.nEdicoes || 0,
  nVisualizacoes: fiscal.nVisualizacoes || 0,
  nVisualizadores: fiscal.nVisualizadores || 0,

  //fiscal
  [`${modulo}Id`]: fiscal[modulo]?.id || fiscal[`${modulo}_id`],
  fiscal: fiscal.fiscal?.nomeCompleto || 'N/A',
  tipoFiscal: getTipoFiscalLabelPorSlug(fiscal.tipoFiscal.toString()) || 'N/A',
  status: fiscal.status == 'S' ? 'Ativo' : 'Inativo',
  documentoSei: fiscal.documentoSei || 'N/A',
  link: fiscal.link || 'N/A',
  dataInicio: fiscal.dataInicio || 'N/A',
  dataFim: fiscal.dataFim || 'N/A',
  observacoes: fiscal.observacoes || 'N/A',
});

export {
  fetchGenericEntityData,
  addResponsaveisAquisicaoSheet,
  addTramitacoesSeiSheet,
  addPartesEnvolvidasSheet,
  addFiscaisAquisicaoSheet,
  addTermosAditivosSheet,
};
